"use client";

import { environmentStorageKey } from "@/lib/appEnvironment"
import { isNativeApp } from "@/lib/platform"
import Link from "next/link";
import { useRef, useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { SpinnerLoading } from "@/components/ui/spinner-loading";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useAuthStore } from "@/store/authStore";
import { usePendingVerificationStore } from "@/store/pendingVerificationStore";
import { ErrorAlert } from "@/components/ui/ErrorAlert";
import { resendVerificationCode } from "@/services/authService";
import { SsoButtons } from "@/components/auth/SsoButtons";
import { ApiError } from "@/types/api";

const LoginSchema = z.object({
  email: z.string().email("Informe um e-mail válido."),
  password: z.string().min(6, "A senha deve ter pelo menos 6 caracteres."),
  rememberMe: z.boolean(),
});

type LoginValues = z.infer<typeof LoginSchema>;

export default function FormLogin() {
  const router = useRouter();
  const { login, loading, error, clearError, user, hydrated } = useAuthStore();
  const lastValues = useRef<LoginValues | null>(null);
  const [notRegistered, setNotRegistered] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (hydrated && user) {
      const lastRoute = localStorage.getItem(environmentStorageKey("dkro_last_route")) || "/workspace";
      router.replace(lastRoute);
    }
  }, [hydrated, user, router]);

  const form = useForm<LoginValues>({
    resolver: zodResolver(LoginSchema),
    // Marcado por padrão: quem usa a plataforma todo dia não deveria ter que pedir para continuar
    // conectado. Quem está num computador compartilhado desmarca.
    defaultValues: { email: "", password: "", rememberMe: true },
    mode: "onSubmit",
  });

  const onSubmit = async (values: LoginValues) => {
    clearError();
    setNotRegistered(false);
    lastValues.current = values;
    try {
      await login(values);
      setRedirecting(true);
      router.push("/workspace");
    } catch (err) {
      // E-mail correto e senha correta, mas conta ainda não verificada: o backend nega o
      // login (403 Identity.EmailNotVerified) antes de emitir qualquer sessão, então não dá
      // pra tratar isso como "sem permissão" e travar a jornada aqui — o caminho é levar
      // direto pra tela de código, já disparando um novo envio.
      if (err instanceof ApiError && err.apiCode === "Identity.EmailNotVerified") {
        clearError();
        usePendingVerificationStore.getState().set(values.email, values.password);
        resendVerificationCode(values.email).catch(() => {
          // Sem problema: a tela de código também tem o botão "enviar novamente".
        });
        router.push("/signup/verify");
        return;
      }

      // Detecta usuário não cadastrado: 404 explícito ou mensagem do backend
      if (
        (err instanceof ApiError && err.code === "NOT_FOUND") ||
        (err instanceof ApiError &&
          /não (foi )?encontrado|not found|não cadastrado|unregistered/i.test(err.message))
      ) {
        setNotRegistered(true);
      }
    }
  };

  const handleRetry = () => {
    if (lastValues.current) {
      onSubmit(lastValues.current);
    }
  };


  const entering = loading || form.formState.isSubmitting || redirecting;

  // Enquanto a sessão é resolvida (hydrate/refresh silencioso), quem já está
  // autenticado não deve ver o formulário: mostra o spinner e o useEffect
  // acima faz o redirect. Sem isso a tela de login pisca antes do redirecionamento.
  if (!hydrated) {
    return <SpinnerLoading delayMs={0} label="Verificando sua sessão..." />;
  }

  return (
    <Card className="auth-card">
      <CardHeader className="space-y-1">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-accent-700 dark:text-accent-400">Bem-vindo de volta</p>
        <CardTitle>Entre na sua conta</CardTitle>
        <p className="pt-2 text-sm leading-6 text-muted-foreground">Continue de onde parou e acompanhe sua operação em tempo real.</p>
      </CardHeader>

      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
            {/* E-mail */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="grid gap-2">
                  <FormLabel htmlFor="email">E-mail</FormLabel>
                  <FormControl>
                    <Input
                      id="email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="voce@exemplo.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Senha */}
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="grid gap-2">
                  <div className="flex items-center justify-between">
                    <FormLabel htmlFor="password">Senha</FormLabel>

                  </div>
                  <FormControl>
                    <Input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      placeholder="••••••••"
                      {...field}
                    />
                  </FormControl>
                    {/* Exemplo: link "esqueceu" */}
                    <Link
                      href="/forgot-password"
                      className="text-sm underline-offset-4 hover:underline link-hover text-right"
                    >
                      Esqueceu sua senha?
                    </Link>                  
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* No app nativo a sessão é sempre persistente — não faz sentido mostrar a opção. */}
            {!isNativeApp() && (
              <FormField
                control={form.control}
                name="rememberMe"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start gap-3">
                    <FormControl>
                      <Checkbox
                        id="rememberMe"
                        checked={field.value}
                        onCheckedChange={(checked) => field.onChange(checked === true)}
                        className="mt-0.5"
                      />
                    </FormControl>
                    <div className="grid gap-1 leading-none">
                      <FormLabel htmlFor="rememberMe" className="cursor-pointer font-normal">
                        Manter conectado neste dispositivo
                      </FormLabel>
                      <p className="text-xs text-muted-foreground">
                        Desmarque em computadores compartilhados: sua sessão será encerrada ao fechar o
                        navegador.
                      </p>
                    </div>
                  </FormItem>
                )}
              />
            )}

            <Button type="submit" variant="default" className="mt-2" disabled={entering} aria-busy={entering}>
              {entering && (
                <span
                  aria-hidden="true"
                  className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
                />
              )}
              {entering ? "Entrando..." : "Entrar na plataforma"}
            </Button>

            {notRegistered ? (
              <div className="rounded-md border border-yellow-400 bg-yellow-50 dark:bg-yellow-950 dark:border-yellow-600 p-4 text-sm space-y-3">
                <p className="text-yellow-800 dark:text-yellow-200 font-medium">
                  Usuário não cadastrado.
                </p>
                <p className="text-yellow-700 dark:text-yellow-300">
                  Nenhuma conta foi encontrada com este e-mail. Deseja criar uma?
                </p>
                <div className="flex gap-2">
                  <Button asChild size="sm" variant="default">
                    <Link href="/signup">Criar conta</Link>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setNotRegistered(false)}
                    type="button"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <ErrorAlert error={error} onRetry={handleRetry} retrying={loading} />
            )}
          </form>
        </Form>

        <SsoButtons intent="login" label="ou entre com" />
      </CardContent>

      <CardFooter className="flex items-center justify-between">
        <p className="text-sm text-center w-full">
          Não possui cadastro?{" "}
          <Link
            href="/signup"
            className="underline underline-offset-4  link-hover"
          >
            Cadastre-se
          </Link>
        </p>
      </CardFooter>
    </Card>
  );
}
